import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import propTypes from "prop-types";
import { i18n } from "lib/i18n";
import classNames from "classnames";

import {
  Dropdown,
  DropdownMenu,
  DSProvider,
  IconButton,
} from "@toddle-edu/ds-web";
import {
  DotsVerticalOutlined,
  PencilLineOutlined,
  DeleteOutlined,
  UserArrowOutlined,
  StudentEvaluateOutlined,
  TagOutlined,
  RightArrowCircleOutlined,
  StudentPendingOutlined,
  CopyOutlined,
  SwitchArrowsOutlined,
  MinusCircleOutlined,
  ReloadArrowOutlined,
  WarningCircleOutlined,
  StudentEditOutlined,
  LockFilled,
  ClockAlarmOutlined,
  EyeOutlined,
  LockOpenOutlined,
  LockOutlined,
  AddToLibraryOutlined,
} from "@toddle-edu/ds-icons";
import CreateOrEditAssignment from "AppComponents/CreateOrEditAssignment";
import TagSnpModalV2 from "AppComponents/TagSnpModalV2";
import TagSnpModalV4 from "AppComponents/TagSnpModalV4";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { useI18n } from "Hooks";
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import {
  deleteAssignmentV2,
  getQueryFiltersMemoize,
  updateAssignment,
  updateStateForAssignment,
  updateStateForAssignmentV2,
  assignmentBulkActionV2,
  getSortedStudents,
} from "ClassRoom/modules/ClassRoomModule";
import {
  isAllowedForCurriculum,
  getCurrentAssessmentTypes,
} from "Platform/modules/PlatformModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { cloneAssessment } from "Assessments/modules/AssessmentModule";
import { getUnitPlanIdFromAssessment } from "Assessments/modules/Utils";
import {
  getIsCurrentAcademicYearSelected,
  getIsArchivedClass,
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import {
  EVIDENCE_TYPE_ASSIGNMENT,
  ASSESSMENT_TYPE_AI_TUTOR,
  ASSESSMENT_TYPE_WORKSHEET,
  CURRICULUM_TYPE_DP,
  TASK_TYPE_AI_TUTOR,
  TASK_TYPE_WORKSHEET,
} from "Constants/stringConstants";

import {
  getIsChangeTaskTypeEnabled,
  getIsEvaluationApplicable,
  getLockedStateToSendWithEvaluationAction,
  getIsPerformAssignmentActionsPermission,
  getIsSendAlertMessageEnabled,
  getSortedStudentSubmissions,
  getSubmissionUnlockState,
} from "ClassRoom/modules/utils";

import AssignmentActionDialog from "AppComponents/AssignmentActionDialog";
import DuplicateAssignmentDialog from "ClassRoom/components/DuplicateAssignmentDialog";
import ChangeTaskType from "ClassRoom/components/ChangeTaskType";

import {
  getGradingOptions,
  getIsGradebookAssignmentDetailedPopoverEnabled,
  updateEvaluatedButNotSharedCountInCategory,
  updateEvaluatedButNotSharedCountInTerm,
} from "Gradebook/modules/Utils";
import {
  getSubjectsWithNoGradebook,
  getIsGradingPeriodUnlocked,
} from "GradingSetup/modules/Utils";
import ACLStorage from "lib/aclService";
import ACLStore from "lib/aclStore";
import {
  ASSIGNMENTS_ACCESS_PERMISSION,
  SNPv2_ACCESS_PERMISSION,
  CAN_EDIT_COURSE_FLOW_PERMISSION,
  ENABLE_AUTH_AND_EVAL_REVAMP,
} from "Constants/permissionConstants";
import withDerivedProps from "lib/withDerivedProps";
import { getOthersDisplaySettings } from "AssessmentsGradebookV2/components/ConfigureViews/components/DisplaySettings/DisplaySettingsUtils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import {
  getIsCreateDuplicateAssignmentIn2P0Enabled,
  getIsEnabledAssignmentImprovements,
} from "Utils/ACLStoreUtils";
import { generateRnPContextsByLevel } from "Utils/RnPHelpers";
import SendAlertMessageModal from "ClassRoom/routes/ClassRoomDetails/routes/AllSubmissions/components/SendAlertMessageModal";
import { getIsWorksheetLayoutUpdateEnabled } from "Worksheet/modules/WorksheetFeatureFlags";

import classes from "./AssignmentActionsDropdown.scss";

const EMPTY_OBJECT = {};

const getStatusCounts = ({ assignedStudents = [], summaryCounts = {} }) => {
  if (!_.isEmpty(summaryCounts)) {
    const assignedCount = _.get(summaryCounts, "assigned", 0);
    const evaluatedCount = _.get(summaryCounts, "evaluated", 0);
    const turnedInCount = _.get(summaryCounts, "turnedIn", 0);
    const evaluationSharedCount = _.get(summaryCounts, "evaluationShared", 0);
    const openSubmissionsCount = _.get(summaryCounts, "unlockedSubmissions", 0);
    const closedSubmissionsCount = _.get(summaryCounts, "lockedSubmissions", 0);
    const evaluationApplicableCount = _.get(
      summaryCounts,
      "evaluationApplicable",
      0
    );

    return {
      notEvaluatedCount: evaluationApplicableCount - evaluatedCount,
      notSubmittedCount: assignedCount - turnedInCount,
      notSharedWithStudentsCount:
        evaluationApplicableCount - evaluationSharedCount,
      openSubmissionsCount,
      closedSubmissionsCount,
    };
  }

  const statusCounts = _.reduce(
    assignedStudents,
    (acc, student) => {
      if (!_.get(student, "isSubmitted", false)) acc.notSubmittedCount++;
      if (_.get(student, "lockingState.state", "") !== "LOCKED")
        acc.openSubmissionsCount++;
      if (_.get(student, "lockingState.state", "") === "LOCKED")
        acc.closedSubmissionsCount++;
      const isEvaluationApplicable = _.get(
        student,
        "isEvaluationApplicable",
        false
      );
      if (!getIsEvaluationApplicable(isEvaluationApplicable)) return acc;

      if (!_.get(student, "isEvaluated", false)) acc.notEvaluatedCount++;
      if (!_.get(student, "isSharedWithStudent", false))
        acc.notSharedWithStudentsCount++;

      return acc;
    },
    {
      notEvaluatedCount: 0,
      notSubmittedCount: 0,
      notSharedWithStudentsCount: 0,
      openSubmissionsCount: 0,
      closedSubmissionsCount: 0,
    }
  );

  return statusCounts;
};

const _getAssignmentActionsDropdownOptions = ({
  t,
  notEvaluatedCount,
  notSharedWithStudentsCount,
  assignmentState,
  isTeacherEvaluationEnabled,
  isPrivate,
  isArchivedClass,
  totalNewSnpCycles,
  isFromClassRoom,
  creationSource,
  isCurrentAcademicYearSelected,
  isClassroomDetailsRoute,
  isClassStreamRoute,
  isClassStreamManagerRoute,
  assessmentType,
  itemDepth,
  showMoveToSectionOption,
  showUpdateStatusOptions,
  showActionOptions,
  isFromDataGrid = false,
  assignment,
  currentCurriculumProgramType,
  isGradingPeriodUnlocked = true,
  isEditCourseFlowAccess,
  canPerformActionsInAssignment,
  openSubmissionsCount,
  closedSubmissionsCount,
  isWorksheetOrAiTutor,
  isStudentSubmissionEnabled,
  isTimedTestEnabled,
  assignedStudents,
  isGradebookAssignmentDetailedPopoverEnabled,
  hideViewInstructions,
  isWorksheet,
  isAssessmentLocked,
}) => {
  const { content } = assignment || EMPTY_OBJECT;

  const {
    assessmentTools = [],
    isLearningGoalsEvaluationEnabled = false,
    isIncludedInCourseFlow = false,
    assignments: { totalCount, userAssignmentsCount } = {},
  } = content || EMPTY_OBJECT;

  const someAssessmentToolTypeHasEval = _.some(
    assessmentTools?.edges,
    ({ node }) => {
      const assessmentToolType = _.get(node, "assessmentToolType", "");

      return _.get(
        _.find(ASSESSMENT_TOOLS, ({ type }) => type === assessmentToolType),
        "hasEvaluation",
        false
      );
    }
  );

  const isFromClassMaterials = creationSource === "CLASS_MATERIALS";
  const isClassroomEnabled = ACLStorage.getModuleSettingsValue(
    ASSIGNMENTS_ACCESS_PERMISSION
  );
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
  const isCreateDuplicateAssignmentIn2P0Enabled = getIsCreateDuplicateAssignmentIn2P0Enabled();

  const disabled = isArchivedClass || !isCurrentAcademicYearSelected;

  const isDisabledWithLockedTermCheck = disabled || !isGradingPeriodUnlocked;

  const disabledTooltipWithLabel = label =>
    !isGradingPeriodUnlocked
      ? t("academicSetup:you_cannot_with_label_since_locked", {
          label,
        })
      : "";

  const subjectType = _.get(assignment, "course.subjects.0.type", "");
  const subjectsWithoutGradebook = getSubjectsWithNoGradebook();
  const hasOnlyQuickTask = _.includes(subjectsWithoutGradebook, subjectType);

  const isAssignmentIncludedInCourseFlow =
    isLearningCoursesEnabled && isIncludedInCourseFlow;
  const isDisabledDueToNoEditCourseFlowAccess =
    isAssignmentIncludedInCourseFlow && !isEditCourseFlowAccess;
  const isDisabledDueToNoAllClassesAccess =
    isLearningCoursesEnabled &&
    !isIncludedInCourseFlow &&
    totalCount !== userAssignmentsCount;
  const disableGenericActions =
    isDisabledDueToNoEditCourseFlowAccess || isDisabledWithLockedTermCheck;
  const disableDeleteAction =
    disableGenericActions || isDisabledDueToNoAllClassesAccess;
  const isDpPractical = _.includes(["dp_pa", "dp_ep"], assessmentType);
  const isEnabledBulkReminder =
    !isPrivate &&
    !isArchivedClass &&
    isCurrentAcademicYearSelected &&
    !_.includes(["DRAFT", "SCHEDULED"], assignmentState) &&
    isGradingPeriodUnlocked &&
    getIsSendAlertMessageEnabled() &&
    canPerformActionsInAssignment;

  const isWorksheetLayoutUpdateEnabled = getIsWorksheetLayoutUpdateEnabled();

  const actionOptions = showActionOptions
    ? [
        {
          key: "VIEW_INSTRUCTIONS",
          label:
            isFromDataGrid || isGradebookAssignmentDetailedPopoverEnabled
              ? t("common:view_with_label", {
                  label: t("common:assignment"),
                })
              : t("common:view"),
          icon: <EyeOutlined variant="subtle" />,
        },
        {
          key: "EDIT",
          label:
            isFromDataGrid || isGradebookAssignmentDetailedPopoverEnabled
              ? t("common:edit_with_label", {
                  label: t("common:assignment"),
                })
              : t("common:edit"),
          disabled: disableGenericActions || isAssessmentLocked,
          isGradingPeriodUnlocked,
          disabledTooltip: isDisabledDueToNoEditCourseFlowAccess
            ? t("classRoom:no_permission_to_manage_course_flow_assigments")
            : isAssessmentLocked
            ? t("course:locked_item_edit_restriction_tooltip", {
                entity: t("common:assignment"),
              })
            : disabledTooltipWithLabel(
                isFromDataGrid || isGradebookAssignmentDetailedPopoverEnabled
                  ? t("common:edit_with_label", {
                      label: t("common:assignment"),
                    })
                  : t("common:edit")
              ),
          icon: <PencilLineOutlined variant="subtle" />,
          suffix: isAssessmentLocked ? <LockFilled variant="subtle" /> : null,
        },
        {
          key: "REASSIGN",
          label:
            assignmentState === "DRAFT"
              ? t("common:assign")
              : isFromDataGrid || isGradebookAssignmentDetailedPopoverEnabled
              ? t("classRoom:edit_assign_settings")
              : t("classRoom:edit_settings"),
          disabled: disableGenericActions,
          isGradingPeriodUnlocked,
          disabledTooltip: isDisabledDueToNoEditCourseFlowAccess
            ? t("classRoom:no_permission_to_manage_course_flow_assigments")
            : disabledTooltipWithLabel(
                assignmentState === "DRAFT"
                  ? t("common:assign")
                  : isFromDataGrid ||
                    isGradebookAssignmentDetailedPopoverEnabled
                  ? t("classRoom:edit_assign_settings")
                  : t("classRoom:edit_settings")
              ),
          icon:
            isFromDataGrid || isGradebookAssignmentDetailedPopoverEnabled ? (
              <StudentEditOutlined variant="subtle" />
            ) : (
              <StudentPendingOutlined variant="subtle" />
            ),
        },
        {
          key: "DUPLICATE",
          label: t("common:copy_to_other_classes"),
          disabled,
          icon: <CopyOutlined variant="subtle" />,
        },
        {
          key: "CREATE_DUPLICATE",
          label: t("common:create_a_duplicate"),
          disabled: disableGenericActions,
          isGradingPeriodUnlocked,
          disabledTooltip: isDisabledDueToNoEditCourseFlowAccess
            ? t("classRoom:no_permission_to_manage_course_flow_assigments")
            : disabledTooltipWithLabel(t("common:create_a_duplicate")),
          icon: <AddToLibraryOutlined variant="subtle" />,
        },
        {
          key: "MOVE_TO_SECTION",
          label:
            itemDepth === 1
              ? t("common:move_to_section")
              : t("common:move_to_another_section"),
          icon: <SwitchArrowsOutlined />,
        },
        {
          key: "CHANGE_TASK",
          label: t("common:change_task_type"),
          disabledTooltip: isDisabledDueToNoEditCourseFlowAccess
            ? t("classRoom:no_permission_to_manage_course_flow_assigments")
            : !isGradingPeriodUnlocked
            ? disabledTooltipWithLabel(t("common:change_task_type"))
            : isAssessmentLocked
            ? t("course:locked_item_edit_restriction_tooltip", {
                entity: t("common:assignment"),
              })
            : t("classRoom:change_task_type_dropdown_disabled_tooltip"),
          disabled:
            assessmentType === "worksheet" ||
            assessmentType === ASSESSMENT_TYPE_AI_TUTOR ||
            disableGenericActions ||
            isAssessmentLocked,
          isGradingPeriodUnlocked,
          icon: <ReloadArrowOutlined variant="subtle" />,
          suffix: isAssessmentLocked ? <LockFilled variant="subtle" /> : null,
        },
        {
          key: "SEND_REMINDER",
          label: t("common:send_a_reminder"),
          disabled,
          disabledTooltip: disabledTooltipWithLabel(
            t("common:send_a_reminder")
          ),
          icon: <ClockAlarmOutlined variant="subtle" />,
        },
        {
          key: "TAG_SNP",
          label: t("common:tag_snp"),
          disabled,
          icon: <TagOutlined variant="subtle" />,
        },
        ...(isFromDataGrid || isGradebookAssignmentDetailedPopoverEnabled
          ? []
          : [{ key: "DIVIDER1", isDivider: true, isGradingPeriodUnlocked }]),
        {
          key: "REMOVE_FROM_CLASS_MATERIALS",
          label: t("classMaterials:remove_from_class_materials"),
          icon: <MinusCircleOutlined variant="subtle" />,
          disabled: isDisabledWithLockedTermCheck,
          isGradingPeriodUnlocked,
          disabledTooltip: disabledTooltipWithLabel(
            t("classMaterials:remove_from_class_materials")
          ),
        },
        {
          key: "DELETE",
          label: t("common:delete"),
          disabled: disableDeleteAction || isAssessmentLocked,
          isGradingPeriodUnlocked,
          disabledTooltip: isDisabledDueToNoEditCourseFlowAccess
            ? t("classRoom:no_permission_to_manage_course_flow_assigments")
            : isDisabledDueToNoAllClassesAccess
            ? t(
                "classRoom:assignment_cannot_be_deleted_due_to_no_all_classes_access"
              )
            : isAssessmentLocked
            ? t("course:locked_item_delete_restriction_tooltip", {
                entity: t("common:assignment"),
              })
            : disabledTooltipWithLabel(t("common:delete")),
          icon: <DeleteOutlined variant="critical" />,
          suffix: isAssessmentLocked ? <LockFilled variant="subtle" /> : null,
          isDestructive: true,
        },
      ]
    : [];

  // filtering out options which are disabled when term is locked
  const actionOptionsWithoutDisabledOptions = _.filter(
    actionOptions,
    actionOption => _.get(actionOption, "isGradingPeriodUnlocked", true)
  );

  const filteredActionOptions = _.filter(
    actionOptionsWithoutDisabledOptions,
    ({ key }) => {
      switch (key) {
        case "TAG_SNP":
          return (
            totalNewSnpCycles > 0 &&
            ACLStorage.getModuleSettingsValue(SNPv2_ACCESS_PERMISSION)
          );
        case "EDIT":
          return (
            creationSource === "CLASSROOM" ||
            isFromClassMaterials ||
            !_.includes(["DRAFT", "SCHEDULED"], assignmentState)
          );
        case "REASSIGN":
        case "DIVIDER": {
          if (isFromClassMaterials) {
            return assignmentState !== "DRAFT";
          }

          return !(
            _.includes(["DRAFT", "SCHEDULED"], assignmentState) &&
            _.includes(
              ["CLASS_STREAM_REVIEW_DETAILS", "CLASSROOM_DETAILS"],
              creationSource
            )
          );
        }
        case "DUPLICATE":
          return (
            assignmentState !== "DRAFT" &&
            !isFromClassMaterials &&
            !isLearningCoursesEnabled
          );

        case "CREATE_DUPLICATE":
          return (
            isCreateDuplicateAssignmentIn2P0Enabled &&
            assignmentState !== "DRAFT" &&
            !isFromClassMaterials &&
            isCurrentAcademicYearSelected &&
            _.get(assignment, "contentType") === "ASSESSMENT" &&
            isLearningCoursesEnabled
          );

        case "MOVE_TO_SECTION":
          return isFromClassMaterials && showMoveToSectionOption;
        case "REMOVE_FROM_CLASS_MATERIALS":
          return isClassroomEnabled && isFromClassMaterials;
        case "CHANGE_TASK":
          return (
            !isDpPractical &&
            isCurrentAcademicYearSelected &&
            getIsChangeTaskTypeEnabled() &&
            (currentCurriculumProgramType !== CURRICULUM_TYPE_DP ||
              (currentCurriculumProgramType === CURRICULUM_TYPE_DP &&
                !hasOnlyQuickTask))
          );
        case "SEND_REMINDER":
          return (
            isEnabledBulkReminder &&
            (_.isEqual(creationSource, "GRADEBOOK") ||
              (isWorksheet && isWorksheetLayoutUpdateEnabled))
          );
        case "VIEW_INSTRUCTIONS":
          return (
            isGradebookAssignmentDetailedPopoverEnabled && !hideViewInstructions
          );
        default:
          return true;
      }
    }
  );

  const archiveLabel = isGradebookAssignmentDetailedPopoverEnabled
    ? t("common:move_to_label_with_preserve_case", {
        label: t("classRoom:reviewed"),
      })
    : t("classRoom:move_to_reviewed");
  const unArchiveLabel = isGradebookAssignmentDetailedPopoverEnabled
    ? t("common:move_to_label_with_preserve_case", {
        label: t("classRoom:to_review_with_hypen"),
      })
    : t("classRoom:move_to_to-review");

  const checkForLockSubmissionAfterEvaluation = !isWorksheetOrAiTutor;

  const isClosedSubmissionsHidden =
    isArchivedClass ||
    !isCurrentAcademicYearSelected ||
    openSubmissionsCount === 0 ||
    (!checkForLockSubmissionAfterEvaluation && notEvaluatedCount === 0);

  const isOpenSubmissionsHidden =
    isArchivedClass ||
    !isCurrentAcademicYearSelected ||
    closedSubmissionsCount === 0 ||
    (!checkForLockSubmissionAfterEvaluation &&
      _.size(assignedStudents) !== notEvaluatedCount);

  const updateStatusOptions = showUpdateStatusOptions
    ? [
        {
          key: "SHARE_WITH_STUDENTS",
          label: t("classRoom:show_post_to_student_and_families"),
          icon: <UserArrowOutlined variant="subtle" />,
          disabled: isDisabledWithLockedTermCheck,
          isGradingPeriodUnlocked,
          disabledTooltip: disabledTooltipWithLabel(
            t("classRoom:show_post_to_student_and_families")
          ),
        },
        {
          key: "EVALUATE_ALL",
          label: isTeacherEvaluationEnabled
            ? t("assessmentEvaluation:mark_all_as_evaluated")
            : t("assessmentEvaluation:mark_all_as_completed"),
          disabled: isDisabledWithLockedTermCheck,
          isGradingPeriodUnlocked,
          disabledTooltip: disabledTooltipWithLabel(
            isTeacherEvaluationEnabled
              ? t("assessmentEvaluation:mark_all_as_evaluated")
              : t("assessmentEvaluation:mark_all_as_completed")
          ),
          icon: <StudentEvaluateOutlined variant="subtle" />,
        },
        {
          key: "SHARE",
          label: t(
            "assessmentEvaluation:share_evaluation_with_students_and_families"
          ),
          disabled: isDisabledWithLockedTermCheck,
          isGradingPeriodUnlocked,
          disabledTooltip: disabledTooltipWithLabel(
            t(
              "assessmentEvaluation:share_evaluation_with_students_and_families"
            )
          ),
          icon: <UserArrowOutlined variant="subtle" />,
        },
        {
          key: "EVALUATE_AND_SHARE",
          label: t(
            "assessmentEvaluation:mark_all_as_evaluated_share_with_students_and_families"
          ),
          disabled: isDisabledWithLockedTermCheck,
          isGradingPeriodUnlocked,
          disabledTooltip: disabledTooltipWithLabel(
            t(
              "assessmentEvaluation:mark_all_as_evaluated_share_with_students_and_families"
            )
          ),
          icon: <UserArrowOutlined variant="subtle" />,
        },
        {
          key: "ARCHIVE",
          label: archiveLabel,
          disabled: isDisabledWithLockedTermCheck,
          isGradingPeriodUnlocked,
          disabledTooltip: disabledTooltipWithLabel(archiveLabel),
          icon: <RightArrowCircleOutlined variant="subtle" />,
        },
        {
          key: "UNARCHIVE",
          label: unArchiveLabel,
          disabled: isDisabledWithLockedTermCheck,
          isGradingPeriodUnlocked,
          disabledTooltip: disabledTooltipWithLabel(unArchiveLabel),
          icon: <RightArrowCircleOutlined variant="subtle" />,
        },
        {
          key: "MARK_AS_PENDING",
          label: t("assessmentEvaluation:mark_evaluation_as_pending"),
          disabled: isDisabledWithLockedTermCheck,
          isGradingPeriodUnlocked,
          disabledTooltip: disabledTooltipWithLabel(
            t("assessmentEvaluation:mark_evaluation_as_pending")
          ),
          icon: <WarningCircleOutlined variant="subtle" />,
        },
        {
          key: "OPEN_SUBMISSIONS",
          label: t("common:open_with_label", {
            label: t("common:submission_plural"),
          }),
          disabled: isDisabledWithLockedTermCheck,
          isGradingPeriodUnlocked,
          disabledTooltip: disabledTooltipWithLabel(
            t("common:open_with_label", {
              label: t("common:submission_plural"),
            })
          ),
          icon: <LockOpenOutlined variant="subtle" />,
        },
        {
          key: "CLOSE_SUBMISSIONS",
          label: t("classRoom:close_submissions"),
          disabled: isDisabledWithLockedTermCheck,
          isGradingPeriodUnlocked,
          disabledTooltip: disabledTooltipWithLabel(
            t("classRoom:close_submissions")
          ),
          icon: <LockOutlined variant="subtle" />,
        },
      ]
    : [];

  // filtering out options which are disabled when term is locked
  const updateStatusOptionsWithoutDisabledOptions = _.filter(
    updateStatusOptions,
    actionOption => _.get(actionOption, "isGradingPeriodUnlocked", true)
  );

  const isEvaluationApplicable =
    isTeacherEvaluationEnabled &&
    (someAssessmentToolTypeHasEval || isLearningGoalsEvaluationEnabled);

  const validStatesForStatusUpdate = _.includes(
    ["PUBLISHED", "ARCHIVED"],
    assignmentState
  );

  const showOpenOrCloseSubmissions =
    isStudentSubmissionEnabled &&
    !isTimedTestEnabled &&
    !isPrivate &&
    isGradebookAssignmentDetailedPopoverEnabled &&
    _.isEqual(creationSource, "GRADEBOOK");

  const filteredUpdateStatusOptions = _.filter(
    updateStatusOptionsWithoutDisabledOptions,
    ({ key }) => {
      switch (key) {
        case "SHARE_WITH_STUDENTS":
          return validStatesForStatusUpdate && isPrivate;
        case "EVALUATE_ALL":
          return validStatesForStatusUpdate && notEvaluatedCount > 0;
        case "SHARE":
          return (
            validStatesForStatusUpdate &&
            isEvaluationApplicable &&
            notEvaluatedCount <= 0 &&
            notSharedWithStudentsCount > 0 &&
            !isPrivate
          );
        case "EVALUATE_AND_SHARE":
          return (
            validStatesForStatusUpdate &&
            isEvaluationApplicable &&
            notEvaluatedCount > 0 &&
            notSharedWithStudentsCount > 0 &&
            !isPrivate
          );
        case "UNARCHIVE":
          return (
            isFromClassRoom &&
            assignmentState === "ARCHIVED" &&
            (isClassroomDetailsRoute
              ? isClassStreamRoute || isClassStreamManagerRoute
              : true)
          );
        case "ARCHIVE":
          return (
            !isDpPractical &&
            isFromClassRoom &&
            assignmentState === "PUBLISHED" &&
            (isClassroomDetailsRoute
              ? isClassStreamRoute || isClassStreamManagerRoute
              : true)
          );
        case "MARK_AS_PENDING":
          return (
            validStatesForStatusUpdate &&
            isEvaluationApplicable &&
            notEvaluatedCount === 0
          );
        case "OPEN_SUBMISSIONS":
          return !isOpenSubmissionsHidden && showOpenOrCloseSubmissions;
        case "CLOSE_SUBMISSIONS":
          return !isClosedSubmissionsHidden && showOpenOrCloseSubmissions;
        default:
          return true;
      }
    }
  );

  const options = [];

  if (
    _.isEqual(creationSource, "GRADEBOOK") &&
    showActionOptions &&
    !isGradebookAssignmentDetailedPopoverEnabled
  ) {
    const gradingOptions = getGradingOptions({
      t,
      disabled,
      isDisabledWithLockedTermCheck,
      hideSort: true,
      hideBulkGrading: !isGradingPeriodUnlocked,
    });

    if (!_.isEmpty(gradingOptions)) {
      options.push({
        label: t("classRoom:grading_options"),
        key: "GRADING_OPTIONS",
        isItemGroup: true,
        options: gradingOptions,
      });
    }
  }

  if (!_.isEmpty(filteredUpdateStatusOptions)) {
    if (_.isEmpty(filteredActionOptions) && !isFromDataGrid) {
      options.push(...filteredUpdateStatusOptions);
    } else {
      options.push({
        label:
          isFromDataGrid || isGradebookAssignmentDetailedPopoverEnabled
            ? t("classRoom:status_options")
            : t("classRoom:update_status"),
        key: "UPDATE_STATUS",
        isItemGroup: true,
        options: filteredUpdateStatusOptions,
      });
    }
  }

  if (!_.isEmpty(filteredActionOptions)) {
    if (_.isEmpty(filteredUpdateStatusOptions) && !isFromDataGrid) {
      options.push(...filteredActionOptions);
    } else {
      options.push({
        label:
          isFromDataGrid || isGradebookAssignmentDetailedPopoverEnabled
            ? t("classRoom:assignment_options")
            : t("common:action_plural"),
        key: "ACTIONS",
        isItemGroup: true,
        options: filteredActionOptions,
      });
    }
  }

  return options;
};

const getAssignmentActionsDropdownOptions = _.memoize(
  _getAssignmentActionsDropdownOptions,
  params => {
    const {
      notEvaluatedCount,
      notSharedWithStudentsCount,
      assignmentState,
      isTeacherEvaluationEnabled,
      isPrivate,
      isArchivedClass,
      totalNewSnpCycles,
      isFromClassRoom,
      creationSource,
      isCurrentAcademicYearSelected,
      isClassroomDetailsRoute,
      isClassStreamRoute,
      isClassStreamManagerRoute,
      assessmentType,
      itemDepth,
      showMoveToSectionOption,
      showUpdateStatusOptions,
      showActionOptions,
      isFromDataGrid = false,
      assignment,
      currentCurriculumProgramType,
      isGradingPeriodUnlocked = true,
      isEditCourseFlowAccess,
      canPerformActionsInAssignment,
      openSubmissionsCount,
      closedSubmissionsCount,
      isWorksheetOrAiTutor,
      isStudentSubmissionEnabled,
      isTimedTestEnabled,
      assignedStudents,
      isGradebookAssignmentDetailedPopoverEnabled,
      hideViewInstructions,
      isWorksheet,
      isAssessmentLocked,
    } = params;

    const assessmentTools = _.get(assignment, "content.assessmentTools.edges");

    // only store the params that are required for memoization to decrease the key size
    return JSON.stringify({
      notEvaluatedCount,
      notSharedWithStudentsCount,
      assignmentState,
      isTeacherEvaluationEnabled,
      isPrivate,
      isArchivedClass,
      totalNewSnpCycles,
      isFromClassRoom,
      creationSource,
      isCurrentAcademicYearSelected,
      isClassroomDetailsRoute,
      isClassStreamRoute,
      isClassStreamManagerRoute,
      assessmentType,
      itemDepth,
      showMoveToSectionOption,
      showUpdateStatusOptions,
      showActionOptions,
      isFromDataGrid,
      currentCurriculumProgramType,
      isGradingPeriodUnlocked,
      isEditCourseFlowAccess,

      assessmentToolIds: _.map(assessmentTools, ({ node }) => node?.id),
      language: i18n.language,
      canPerformActionsInAssignment,
      openSubmissionsCount,
      closedSubmissionsCount,
      isWorksheetOrAiTutor,
      isStudentSubmissionEnabled,
      isTimedTestEnabled,
      assignedStudents,
      isGradebookAssignmentDetailedPopoverEnabled,
      hideViewInstructions,
      isWorksheet,
      isAssessmentLocked,
    });
  }
);

const AssignmentActionsDropdown = props => {
  const {
    assignment,
    buttonComponent,
    isCurrentAcademicYearSelected,
    organizationResourceId,
    isArchivedClass,
    onClickMenuItem,
    totalNewSnpCycles,
    isAllowedForCurriculum,
    course,
    creationSource,
    visible,
    onVisibleChange,
    deleteAssignmentV2,
    studentWiseRatingsVariables,
    organizationAssignmentVariables,
    curriculumProgramId,
    filters,
    updateAssignment,
    updateStateForAssignment,
    updateStateForAssignmentV2,
    assignmentBulkActionV2,
    overlayStyle,
    itemTitleContainerStyle,
    onActionDone,
    buttonSize,
    buttonVariant,
    getCurrentAssessmentTypes,
    getPopupContainer,
    setToastMsg,
    cloneAssessment,
    courseId,
    viewType,
    isClassStreamRoute,
    isClassStreamManagerRoute,
    isClassroomDetailsRoute,
    currentCurriculumProgramType,
    dsVersion = "1.0",
    buttonType,
    itemDepth = 1,
    showMoveToSectionOption,
    showUpdateStatusOptions,
    showActionOptions,
    testId = _.camelCase(viewType) ?? "classRoom",
    isFromDataGrid = false,
    academicYearId,
    icon,
    hasCategories,
    gradebookSelectedSubject,
    studentIds,
    gradebookSelectedTermAcademicYearId,
    gradebookSelectedTermId,
    skipCountOfEvaluatedButNotSharedAssignments,
    showOtherClassesTasks,
    disabled,
    isTimedTestEnabled,
    externalActionControl,
    getSortedStudents,
    isLinksRoute,
    isMediaManagerRoute,
    isGradebookRoute,
    isClassroomRoute,
    isClassflowRoute,
    onPreviewClick,
    hideViewInstructions = false,
    onOrganizationResourceDelete,
    showAssignmentCopyLinkOption,
    canManageAssignments,
  } = props;

  const { t } = useI18n();

  const [currentAction, setCurrentAction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [clonedAssessmentId, setClonedAssessmentId] = useState(null);

  const isGradebookAssignmentDetailedPopoverEnabled = getIsGradebookAssignmentDetailedPopoverEnabled();
  const {
    id,
    state: { state: assignmentState } = {},
    assignedStudents,
    isTeacherEvaluationEnabled,
    isStudentSubmissionEnabled,
    isPrivate = false,
    content,
    counts: { totalAssignedStudents = 0, totalDraftSubmissions = 0 } = {},
    summaryCounts,
  } = assignment || {};
  const learningCourse = _.first(
    _.get(assignment, "course.learningCourse", [])
  );
  const learningCourseId = _.get(learningCourse, "id", null);

  const sortedAssignedStudents = getSortedStudentSubmissions({
    assignedStudents,
    getSortedStudents,
  });

  const {
    notEvaluatedCount,
    notSubmittedCount,
    notSharedWithStudentsCount,
    openSubmissionsCount,
    closedSubmissionsCount,
  } = getStatusCounts({ assignedStudents, summaryCounts });

  const currentAssessmentTypes = getCurrentAssessmentTypes();
  const assessmentType =
    _.get(assignment, "content.assessmentType.value", "le") || "le";
  const taskTypeGroup = _.get(assignment, "content.taskType.type");
  const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
    _.get(content, "academicTerm"),
    assessmentType
  );
  const assessmentTypeObj = currentAssessmentTypes[assessmentType];
  const assessmentTypeLocale = assessmentTypeObj?.localeLabel;
  const assessmentTypeLabel = assessmentTypeLocale
    ? t(assessmentTypeLocale)
    : assessmentTypeObj?.label;

  const isWorksheetOrAiTutor =
    _.includes(
      [ASSESSMENT_TYPE_WORKSHEET, ASSESSMENT_TYPE_AI_TUTOR],
      assessmentType
    ) || _.includes([TASK_TYPE_WORKSHEET, TASK_TYPE_AI_TUTOR], taskTypeGroup);

  const isWorksheet = _.isEqual(assessmentType, ASSESSMENT_TYPE_WORKSHEET);

  const isAssessmentLocked = _.get(content, "lockingInfo.isLocked", false);

  const isFromClassRoom = _.includes(
    [
      "CLASSROOM",
      "CLASSROOM_DETAILS",
      "CLASS_STREAM_REVIEW",
      "CLASS_STREAM_REVIEW_DETAILS",
    ],
    creationSource
  );

  const updateEvaluatedButNotSharedCount = evaluatedButNotSharedCount => {
    if (
      !isFromDataGrid ||
      skipCountOfEvaluatedButNotSharedAssignments ||
      evaluatedButNotSharedCount === 0
    ) {
      return;
    }

    const commonParams = {
      courseId,
      curriculumType: currentCurriculumProgramType,
      primaryGradeId: _.get(course, "primaryGrade.id"),
      selectedSubject: gradebookSelectedSubject,
      studentIds,
      showOtherClassesTasks,
      curriculumProgramId,
    };

    if (hasCategories) {
      updateEvaluatedButNotSharedCountInCategory({
        id: _.get(content, "category.id"),
        count: evaluatedButNotSharedCount,
        ...commonParams,
      });
    }

    updateEvaluatedButNotSharedCountInTerm({
      academicYearId: gradebookSelectedTermAcademicYearId,
      count: evaluatedButNotSharedCount,
      gradebookSelectedTermId,
      ...commonParams,
    });
  };

  const canPerformActionsInAssignment = getIsPerformAssignmentActionsPermission(
    {
      assessmentType,
      courseId: _.get(assignment, "course.id", null),
      curriculumProgramId,
      gradeId: _.get(assignment, "course.primaryGrade.id", null),
      learningCourseId: _.get(assignment, "course.learningCourse.0.id", null),
    }
  );

  const isManageCourseFlowPermission = _.get(
    assignment,
    "isManageCourseFlowPermission",
    false
  );
  const isEditCourseFlowAccess = useMemo(() => {
    if (isManageCourseFlowPermission) return true;
    const [learningCourseContext] = generateRnPContextsByLevel({
      level: "LEARNING_COURSE",
      learningCourseId,
      curriculumProgramId,
    });
    return ACLStorage.getPermissionValueByContext(
      CAN_EDIT_COURSE_FLOW_PERMISSION,
      learningCourseContext
    );
  }, [learningCourseId, curriculumProgramId, isManageCourseFlowPermission]);

  const filteredOptions = getAssignmentActionsDropdownOptions({
    t,
    notEvaluatedCount,
    notSharedWithStudentsCount,
    assignmentState,
    isTeacherEvaluationEnabled,
    isPrivate,
    isArchivedClass,
    totalNewSnpCycles,
    isAllowedForCurriculum,
    isFromClassRoom,
    creationSource,
    isCurrentAcademicYearSelected,
    isClassroomDetailsRoute,
    isClassStreamRoute,
    isClassStreamManagerRoute,
    currentCurriculumProgramType,
    assessmentType,
    itemDepth,
    showMoveToSectionOption,
    onClickMenuItem,
    showUpdateStatusOptions,
    showActionOptions,
    isGradingPeriodUnlocked,
    isFromDataGrid,
    assignment,
    isEditCourseFlowAccess,
    canPerformActionsInAssignment,
    openSubmissionsCount,
    closedSubmissionsCount,
    isWorksheetOrAiTutor,
    isStudentSubmissionEnabled,
    isTimedTestEnabled,
    assignedStudents,
    isGradebookAssignmentDetailedPopoverEnabled,
    hideViewInstructions,
    isWorksheet,
    isAssessmentLocked,
  });

  const showDropdown = !_.isEmpty(filteredOptions);

  const onClose = isSuccess => {
    if (isSuccess && onActionDone && onActionDone[currentAction]) {
      onActionDone[currentAction]();
    }
    setCurrentAction(null);
    setClonedAssessmentId(null);

    externalActionControl?.onClose?.();
  };

  const onClickDialogConfirm = async params => {
    const { moveToReviewed, closeSubmissions } = params || {};

    setIsLoading(true);

    const lockState = getLockedStateToSendWithEvaluationAction({
      closeSubmissions,
      isWorksheetOrAiTutor,
      isStudentSubmissionEnabled,
      isPrivate,
      isTimedTestEnabled,
    });

    // * used count for updating value in cache, count of assignments that are evaluated but not shared in that case we will show info icon tooltip over term and category total columns in gradebook datagrid
    let evaluatedButNotSharedCount = 0;

    let toastMsg = "";

    switch (currentAction) {
      case "DELETE":
        await deleteAssignmentV2({
          id,
          creationSource,
          organizationAssignmentVariables,
        });

        break;
      case "SHARE_WITH_STUDENTS":
        {
          await updateAssignment({
            id,
            isPrivate: false,
            courses: {
              updatedCourses: [
                {
                  courseId: _.get(assignment, "course.id"),
                  visibility: "VISIBLE",
                },
              ],
            },
          });

          // * removing 1 as assignment now evaluated and shared
          evaluatedButNotSharedCount = -1;
        }
        break;
      case "EVALUATE":
        {
          await assignmentBulkActionV2({
            id,
            isEvaluated: true,
            isMoveToArchived: moveToReviewed,
            creationSource,
            action: currentAction,
            organizationResourceId,
            isTeacherEvaluationEnabled,
            studentWiseRatingsVariables,
            lockState,
            course,
            assessmentDetails: content,
            assignedStudents,
          });

          // * evaluated but not shared so adding 1
          evaluatedButNotSharedCount = 1;
          toastMsg = t("toastMsgs:all_students_marked_as_evaluated");
        }
        break;
      case "EVALUATE_SHARE":
        {
          await assignmentBulkActionV2({
            id,
            isEvaluated: true,
            isSharedWithStudent: true,
            isMoveToArchived: moveToReviewed,
            creationSource,
            action: currentAction,
            isTeacherEvaluationEnabled,
            organizationResourceId,
            studentWiseRatingsVariables,
            lockState,
          });
          toastMsg = t("toastMsgs:all_students_marked_as_evaluated_and_shared");
        }
        break;
      case "SHARE": {
        await assignmentBulkActionV2({
          id,
          isSharedWithStudent: true,
          isMoveToArchived: moveToReviewed,
          creationSource,
          action: currentAction,
          isTeacherEvaluationEnabled,
          organizationResourceId,
          studentWiseRatingsVariables,
          lockState,
        });

        // * removing 1 as assignment now evaluated and shared
        evaluatedButNotSharedCount = -1;
      }
    }

    if (onActionDone && onActionDone[currentAction]) {
      onActionDone[currentAction]();
    }

    updateEvaluatedButNotSharedCount(evaluatedButNotSharedCount);
    setIsLoading(false);
    if (!_.isEmpty(toastMsg) && isGradebookAssignmentDetailedPopoverEnabled) {
      setToastMsg({
        type: "success",
        msg: toastMsg,
      });
    }
    onClose();
  };

  const onClickUnArchive = async id => {
    try {
      await updateStateForAssignment({
        id,
        state: "PUBLISHED",
        creationSource,
      });
      setToastMsg({
        msg: "toastMsgs:post_moved_ongoing",
        type: "success",
      });
    } catch (e) {
      if (e.networkError) {
        setToastMsg("toastMsgs:no_internet_connection");
      } else {
        setToastMsg("toastMsgs:something_went_wrong");
      }
    }
  };

  useEffect(() => {
    /**
     * if externalActionControl prop is passed then we will perform the action based on the type passed in the externalActionControl prop
     * this is used in the case where we want to perform the action from outside the component
     *
     */
    if (
      _.includes(
        externalActionControl?.supportedActions,
        externalActionControl?.type
      )
    ) {
      onClickItem({ key: externalActionControl.type });
    }
  }, [externalActionControl?.type]);

  const onClickItem = async ({ key, domEvent }) => {
    domEvent?.stopPropagation();

    if (onClickMenuItem && onClickMenuItem[key]) {
      onClickMenuItem[key]();
      return;
    }

    const assignmentStateTab = _.get(
      filters,
      "assignmentStateTab",
      "PUBLISHED"
    );

    // * used count for updating value in cache, count of assignments that are evaluated but not shared in that case we will show info icon tooltip over term and category total columns in gradebook datagrid
    let evaluatedButNotSharedCount = 0;

    // we are skipping loading on updating the assignment status in the new flow
    if (
      !getIsEnabledAssignmentImprovements() ||
      !_.includes(["ARCHIVE", "UNARCHIVE"], key)
    ) {
      setIsLoading(true);
    }

    let toastMsg = "";

    switch (key) {
      case "unarchive":
        await onClickUnArchive(id);
        break;
      case "ARCHIVE":
      case "UNARCHIVE": {
        externalActionControl?.onClose?.();
        await updateStateForAssignmentV2({
          id,
          state: assignmentState === "PUBLISHED" ? "ARCHIVED" : "PUBLISHED",
          creationSource,
          oldState: assignmentState,
          shouldSkipCacheUpdate: !_.includes(
            ["PUBLISHED", "ARCHIVED"],
            assignmentStateTab
          ),
          assignmentName: _.get(content, "title.value", ""),
          shouldUseCacheUpdateFlow: getIsEnabledAssignmentImprovements(),
        });
        break;
      }
      case "evaluate_all":
        setCurrentAction("EVALUATE");
        break;

      case "evaluate_and_share":
        setCurrentAction("EVALUATE_SHARE");
        break;

      case "share":
        setCurrentAction("SHARE");
        break;

      case "EVALUATE_ALL":
        setCurrentAction("EVALUATE");
        break;

      case "EVALUATE_AND_SHARE":
        setCurrentAction("EVALUATE_SHARE");
        break;

      case "SHARE":
        setCurrentAction("SHARE");
        break;

      case "MARK_AS_PENDING":
        {
          await assignmentBulkActionV2({
            id,
            isEvaluated: false,
            isSharedWithStudent: false,
            creationSource,
            organizationResourceId,
            studentWiseRatingsVariables,
          });

          externalActionControl?.onClose?.();

          // removing 1 as assignment now not evaluated and shared
          evaluatedButNotSharedCount = -1;

          toastMsg = t("toastMsgs:all_students_marked_as_pending");
        }
        break;

      case "duplicate":
      case "DUPLICATE": {
        const { clonedAssessmentId } = await cloneAssessment({
          sourceAssessmentId: _.get(assignment, "content.id", null),
          portalType: "PLANNER",
          academicYears: _.map(
            _.get(course, "academicYears", []),
            ({ id }) => id
          ),
          unitPlanId: getUnitPlanIdFromAssessment({
            assessmentDetails: _.get(assignment, "content", {}),
          }),
          title: _.get(assignment, "content.title.value", ""),
        });
        if (!_.isEmpty(clonedAssessmentId)) {
          setClonedAssessmentId(clonedAssessmentId);
          setCurrentAction(key);
        }
        break;
      }

      case "CREATE_DUPLICATE": {
        setCurrentAction("CREATE_DUPLICATE");
        break;
      }

      case "delete":
      case "DELETE":
        setCurrentAction("DELETE");
        break;
      case "SEND_REMINDER":
        setCurrentAction("SEND_REMINDER");
        break;
      case "OPEN_SUBMISSIONS": {
        const lockState = getSubmissionUnlockState({
          isLockSubmissionEnabled: _.get(
            assignment,
            "isLockSubmissionEnabled",
            false
          ),
          deadline: _.get(assignment, "deadline", {}),
        });

        await assignmentBulkActionV2({
          id: _.get(assignment, "id"),
          lockState,
          creationSource,
          studentAssignmentIds: _.map(assignedStudents, "id"),
        });

        toastMsg = t("toastMsgs:submissions_open_for", {
          taskName: _.get(assignment, "content.title.value", ""),
        });

        externalActionControl?.onClose?.();
        break;
      }
      case "CLOSE_SUBMISSIONS": {
        await assignmentBulkActionV2({
          id: _.get(assignment, "id"),
          lockState: "LOCKED",
          studentAssignmentIds: _.map(assignedStudents, "id"),
          assignedStudents,
          creationSource,
          assessmentDetails: _.get(assignment, "content"),
        });

        toastMsg = t("toastMsgs:submissions_closed_for", {
          taskName: _.get(assignment, "content.title.value", ""),
        });

        externalActionControl?.onClose?.();
        break;
      }
      case "VIEW_INSTRUCTIONS": {
        setCurrentAction("VIEW_INSTRUCTIONS");
        break;
      }

      default:
        setCurrentAction(key);
    }

    updateEvaluatedButNotSharedCount(evaluatedButNotSharedCount);
    if (!_.isEmpty(toastMsg) && isGradebookAssignmentDetailedPopoverEnabled) {
      setToastMsg({
        type: "success",
        msg: toastMsg,
      });
    }
    setIsLoading(false);

    // to set the isVisible to false after the action is performed
    if (_.isFunction(onClickMenuItem)) {
      onClickMenuItem(key);
      return;
    }
  };

  const renderTagSnpModalV2Element = () => {
    const taggedPractices = assignment?.taggedPractices ?? [];

    const itemType =
      _.get(assignment, "content.assessmentType.value", "") ||
      _.get(assignment, "contentType", "") ||
      "ASSESSMENT";

    const itemTitle =
      _.get(assignment, "content.title.value", "") ||
      _.get(assignment, "content.assignmentTitle", "") ||
      "";

    const assignmentFeedFilters = _.pick(
      getQueryFiltersMemoize({ filters, academicYearId }),
      ["unreadMessageCountFilters", "userArchivedFilters"]
    );

    const isAuthAndEvalRevampEnabled = ACLStore.can(
      ENABLE_AUTH_AND_EVAL_REVAMP
    );

    return (
      <div className={classes.container} onClick={e => e?.stopPropagation()}>
        {isAuthAndEvalRevampEnabled ? (
          <TagSnpModalV4
            itemGroupType={EVIDENCE_TYPE_ASSIGNMENT}
            itemType={itemType}
            itemTitle={itemTitle}
            taggedPractices={taggedPractices}
            curriculumProgramId={curriculumProgramId}
            closeModal={onClose}
            itemId={assignment?.id}
            assignmentFeedFilters={assignmentFeedFilters}
            testId={testId}
          />
        ) : (
          <DSProvider dsVersion="1.0">
            <TagSnpModalV2
              itemGroupType={EVIDENCE_TYPE_ASSIGNMENT}
              itemType={itemType}
              itemTitle={itemTitle}
              taggedPractices={taggedPractices}
              curriculumProgramId={curriculumProgramId}
              closeModal={onClose}
              thumbnailData={{}}
              itemId={assignment?.id}
              assignmentFeedFilters={assignmentFeedFilters}
              testId={testId}
            />
          </DSProvider>
        )}
      </div>
    );
  };

  const renderDialogBox = () => {
    const allowMovingTaskToReviewed =
      _.includes(
        [
          "CLASSROOM",
          "CLASS_STREAM_REVIEW",
          "CLASS_STREAM_REVIEW_DETAILS",
          "CLASSROOM_DETAILS",
        ],
        creationSource
      ) &&
      (isClassroomDetailsRoute
        ? isClassStreamRoute || isClassStreamManagerRoute
        : true) &&
      assignmentState === "PUBLISHED";

    const itemTitle =
      _.get(assignment, "content.title.value", "") ||
      _.get(assignment, "content.assignmentTitle", "") ||
      "";

    const isSubmissionLocked = openSubmissionsCount === 0;

    return (
      <div onClick={e => e?.stopPropagation()}>
        <AssignmentActionDialog
          dialogAction={currentAction}
          isBulk={true}
          isTeacherEvaluationEnabled={isTeacherEvaluationEnabled}
          isStudentSubmissionEnabled={isStudentSubmissionEnabled}
          isPrivate={isPrivate}
          totalStudents={totalAssignedStudents}
          selectedStudentsCount={totalAssignedStudents}
          notSubmittedCount={notSubmittedCount}
          isSubmissionLocked={isSubmissionLocked}
          draftSubmissionsCount={totalDraftSubmissions}
          openSubmissionsCount={openSubmissionsCount}
          notEvaluatedCount={notEvaluatedCount}
          notSharedWithStudentsCount={notSharedWithStudentsCount}
          assessmentTypeLabel={assessmentTypeLabel}
          allowMovingTaskToReviewed={allowMovingTaskToReviewed}
          handleCancelClick={onClose}
          handleConfirmClick={onClickDialogConfirm}
          assignmentTitle={itemTitle}
          viewType={viewType}
          testId={testId}
          totalLinkedAssignments={_.get(content, "assignments.totalCount")}
          courseTitle={_.get(course, "title")}
          allLinkedClasses={_.map(
            _.get(content, "assignments.edges", []),
            "course"
          )}
          isIncludedInCourseFlow={_.get(
            content,
            "isIncludedInCourseFlow",
            false
          )}
          isWorksheetOrAiTutor={isWorksheetOrAiTutor}
        />
      </div>
    );
  };

  const renderAddAssignment = () => {
    const transparentModalClass =
      currentAction === "REASSIGN" ? classes.transparentModal : "";

    const assignmentCourseId = _.get(assignment, "course.id", null);
    const assignmentCourse = _.get(assignment, "course", {});

    return (
      <CreateOrEditAssignment
        mode={
          _.includes(["edit", "EDIT", "REASSIGN"], currentAction)
            ? "edit"
            : "duplicate"
        }
        assignment={assignment}
        course={creationSource === "GRADEBOOK" ? assignmentCourse : course}
        courseId={
          creationSource === "GRADEBOOK"
            ? assignmentCourseId
            : course?.id || courseId
        }
        creationSource={
          _.includes(
            ["CLASS_STREAM_REVIEW_DETAILS", "CLASSROOM_DETAILS"],
            creationSource
          )
            ? "CLASSROOM"
            : creationSource
        }
        creationSubSource={creationSource}
        onClose={onClose}
        isSettingsStepOnly={currentAction === "REASSIGN"}
        modalContent={transparentModalClass}
        overlayContainer={transparentModalClass}
        clonedAssessmentId={clonedAssessmentId}
        testId={testId}
      />
    );
  };

  const renderChangeTaskTypeModal = () => {
    return (
      <div onClick={e => e?.stopPropagation()}>
        <ChangeTaskType
          handleClose={onClose}
          assessmentTypeObj={_.get(content, "assessmentType", null)}
          assessmentId={_.get(assignment, "content.id", null)}
          pageType={
            creationSource === "CLASS_STREAM_REVIEW"
              ? "assignmentManager"
              : viewType
          }
          assessmentTools={_.get(content, "assessmentTools.edges", [])}
          assignmentId={assignment?.id}
          title={_.get(assignment, "content.title", null)}
          isLearningGoalsEvaluationEnabled={_.get(
            content,
            "isLearningGoalsEvaluationEnabled",
            false
          )}
        />
      </div>
    );
  };

  const renderSendAReminderModal = () => {
    return (
      <SendAlertMessageModal
        setSendAlertMessageModalParams={() => {
          setCurrentAction(null);

          externalActionControl?.onClose?.();
        }}
        selectedStudentsSubmissions={[]}
        sendAlertMessageModalParams={{ type: "reminder" }}
        sortedAssignedStudents={sortedAssignedStudents}
        assignmentDeadline={_.get(assignment, "deadline", {})}
        assignmentId={_.get(assignment, "id", null)}
        isStudentSubmissionEnabled={isStudentSubmissionEnabled}
        isBackdateAssignment={_.get(assignment, "isBackdateAssignment", false)}
      />
    );
  };

  const renderCompactLePreview = () => {
    onPreviewClick({
      assessmentId: _.get(assignment, "content.id", null),
      assignmentId: _.get(assignment, "id", null),
      portalType: "PLANNER",
      showAttachmentItemV3: true,
      showCloseButton: true,
      showFullScreenButton: false,
      onCloseClickAction: () => {
        setCurrentAction(null);
      },
      attachmentItemHeight: 100,
      isFullScreenLE: true,
      showEmptyState: true,
      customFullScreenUpperCon: classes.customFullScreenUpperCon,
      showAssignmentActionsButton: true,
      assignmentData: assignment,
      courseData: course,
      isMediaManagerRoute: isMediaManagerRoute,
      totalNewSnpCycles: totalNewSnpCycles,
      isClassStreamManagerRoute: isClassStreamManagerRoute,
      courseId: _.get(course, "id", null),
      isLinksRoute: isLinksRoute,
      canPerformActionsInAssignment:
        canManageAssignments ?? canPerformActionsInAssignment,
      isGradebookRoute: isGradebookRoute,
      isClassroomRoute: isClassroomRoute,
      isClassflowRoute: isClassflowRoute,
      onOrganizationResourceDelete: onOrganizationResourceDelete,
      showAssignmentCopyLinkOption: showAssignmentCopyLinkOption,
    });
  };

  const renderDuplicateAssignmentDialog = () => {
    return (
      <DuplicateAssignmentDialog
        assignment={assignment}
        course={course}
        courseId={courseId}
        learningCourseId={learningCourseId}
        onClose={onClose}
      />
    );
  };

  const renderCurrentActionComponent = () => {
    if (!currentAction) {
      return null;
    }
    switch (currentAction) {
      case "edit":
      case "EDIT":
      case "REASSIGN":
      case "duplicate":
      case "DUPLICATE":
        return renderAddAssignment();
      case "CREATE_DUPLICATE":
        return renderDuplicateAssignmentDialog();
      case "DELETE":
      case "SHARE_WITH_STUDENTS":
      case "EVALUATE":
      case "EVALUATE_SHARE":
      case "SHARE":
        return renderDialogBox();
      case "TAG_SNP":
        return renderTagSnpModalV2Element();
      case "CHANGE_TASK":
        return renderChangeTaskTypeModal();
      case "SEND_REMINDER":
        return renderSendAReminderModal();
      case "VIEW_INSTRUCTIONS":
        return renderCompactLePreview();
      default:
        return null;
    }
  };

  const dropdownMenuClasses = classNames({
    [classes.dropdownMenuRevamp]: isGradebookAssignmentDetailedPopoverEnabled,
  });

  // Wrapper stops dropdown/menu/modal events bubbling to parent row handlers.
  return (
    <div
      role="none"
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      {showDropdown && !externalActionControl ? (
        <Dropdown
          overlay={
            <div className={dropdownMenuClasses}>
              <DropdownMenu
                options={filteredOptions}
                onClick={onClickItem}
                testId={`${testId}-assignmentActions`}
                dsVersion={dsVersion}
                itemTitleContainerStyle={itemTitleContainerStyle}
              />
            </div>
          }
          visible={visible}
          onVisibleChange={onVisibleChange}
          overlayStyle={overlayStyle}
          getPopupContainer={getPopupContainer}
          placement={"bottomRight"}
          disabled={disabled}
        >
          {buttonComponent || (
            <IconButton
              type={buttonType}
              dsVersion={dsVersion}
              className="relative"
              variant={buttonVariant}
              testId={`${testId}-assignmentActions`}
              size={buttonSize}
              icon={icon || <DotsVerticalOutlined size="xx-small" />}
            />
          )}
        </Dropdown>
      ) : null}
      {renderCurrentActionComponent()}
      {isLoading ? (
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      ) : null}
    </div>
  );
};

AssignmentActionsDropdown.propTypes = {
  visible: propTypes.bool,
  onVisibleChange: propTypes.func,
  studentWiseRatingsVariables: propTypes.object,
  organizationAssignmentVariables: propTypes.object,
  creationSource: propTypes.string,
  overlayStyle: propTypes.object,
  itemTitleContainerStyle: propTypes.object,
  buttonSize: propTypes.string,
  buttonVariant: propTypes.string,
  getPopupContainer: propTypes.func,
  showUpdateStatusOptions: propTypes.bool,
  showActionOptions: propTypes.bool,
  isFromDataGrid: propTypes.bool,
  hasCategories: propTypes.bool,
  disabled: propTypes.bool,
  onShowCompactLEPreview: propTypes.func,
};

AssignmentActionsDropdown.defaultProps = {
  visible: false,
  onVisibleChange: () => {},
  studentWiseRatingsVariables: {},
  organizationAssignmentVariables: {},
  creationSource: "CLASSROOM",
  overlayStyle: {},
  itemTitleContainerStyle: {},
  buttonSize: "medium",
  buttonVariant: "plain",
  getPopupContainer: el => el,
  showUpdateStatusOptions: true,
  showActionOptions: true,
  disabled: false,
  onShowCompactLEPreview: _.noop,
};

const mapStateToProps = state => {
  const curriculumProgramId = getSelectedCurriculumId(state);
  return {
    curriculumProgramId,
    isCurrentAcademicYearSelected: getIsCurrentAcademicYearSelected(state),
    isArchivedClass: getIsArchivedClass(state),
    filters: _.get(state, "classRoom.filters"),
    currentCurriculumProgramType: getCurrentCurriculumProgramType(state),
    academicYearId: getSelectedAcademicYearId(state),
    gradebookSelectedSubject: _.get(
      state,
      "gradebook.assessmentsFilters.selectedSubject"
    ),
    gradebookSelectedTermAcademicYearId: _.get(
      state,
      "gradebook.selectedAcademicYearId"
    ),
    displaySettings: _.get(state, "gradebook.displaySettings", EMPTY_OBJECT),
  };
};

const getDerivedProps = ownProps => {
  const { displaySettings } = ownProps;

  const { showOtherClassesTasks } = getOthersDisplaySettings(displaySettings);

  return {
    isClassroomDetailsRoute: location.href.search("classroom-details") > -1,
    isClassStreamManagerRoute:
      location.href.search("class-stream-manager/classroom-details") > -1,
    isClassStreamRoute:
      location.href.search("classroom/classroom-details") > -1,
    showOtherClassesTasks,
    isLinksRoute: location.href.search("links") > -1,
    isMediaManagerRoute: location.href.search("mediamanager") > -1,
    isGradebookRoute: location.href.search("gradebook") > -1,
    isClassroomRoute:
      location.href.search("/classroom") > -1 &&
      location.href.search("classroom-details") === -1,
    isClassflowRoute:
      location.href.search("/class-flow") > -1 &&
      location.href.search("classroom-details") === -1,
  };
};

const mapActionCreators = {
  isAllowedForCurriculum,
  deleteAssignmentV2,
  updateAssignment,
  updateStateForAssignment,
  updateStateForAssignmentV2,
  assignmentBulkActionV2,
  getCurrentAssessmentTypes,
  setToastMsg,
  cloneAssessment,
  getSortedStudents,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps)
)(AssignmentActionsDropdown);
